import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, Sun, Moon, Wifi, Battery } from "lucide-react";
import { Button } from "@/components/ui/button";
import RobotFace from "@/components/RobotFace";
import { RobotBody } from "@/components/RobotBody";
import { useAuth } from "@/lib/auth";

const EXPRS = [
  { id: "idle", label: "Idle" },
  { id: "listening", label: "Listening" },
  { id: "thinking", label: "Thinking" },
  { id: "speaking", label: "Speaking" },
  { id: "happy", label: "Happy" },
  { id: "sad", label: "Sad" },
  { id: "curious", label: "Curious" },
  { id: "excited", label: "Excited" },
  { id: "sleeping", label: "Sleeping" },
];

export default function Device() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [expr, setExpr] = useState("idle");
  const [round, setRound] = useState(false);
  const [showBody, setShowBody] = useState(true);
  const [micMuted, setMicMuted] = useState(false);
  const [battery, setBattery] = useState(78);

  return (
    <div className="min-h-screen text-white" style={{
      background: "radial-gradient(1200px 700px at 30% 20%, #16181d, #0B0C10 70%)"
    }} data-testid="page-device">
      <header className="p-6 flex items-center justify-between">
        <button onClick={()=>nav(-1)} className="flex items-center gap-1 text-sm text-white/70 hover:text-white" data-testid="device-back">
          <ArrowLeft size={16}/> Back
        </button>
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center">
            <div className="w-2 h-2 rounded-full bg-[var(--rammy-cyan)] shadow-[0_0_10px_var(--rammy-cyan)]"/>
          </div>
          <span className="font-display">Rammy</span>
        </Link>
      </header>

      <main className="max-w-6xl mx-auto px-6 pb-24 grid lg:grid-cols-2 gap-16 items-center">
        <section className="fade-up">
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--rammy-cyan)] font-semibold">Robot face simulator</div>
          <h1 className="mt-3 text-5xl font-display leading-tight">See Rammy's<br/>emotional range.</h1>
          <p className="mt-6 text-white/70 max-w-md leading-relaxed">
            This is the exact digital face that will render on Rammy's 3.5–5&nbsp;inch display.
            Try every expression, toggle the mic, and preview the round-display variant.
          </p>

          <div className="mt-8 grid grid-cols-3 gap-2 max-w-md">
            {EXPRS.map(e => (
              <button
                key={e.id}
                onClick={()=>setExpr(e.id)}
                data-testid={`device-expr-${e.id}`}
                className={`px-3 py-2.5 rounded-2xl border text-sm transition ${
                  expr===e.id
                    ? "bg-[var(--rammy-cyan)] text-[var(--rammy-text)] border-[var(--rammy-cyan)]"
                    : "border-white/15 text-white/80 hover:border-white/40"
                }`}
              >{e.label}</button>
            ))}
          </div>

          <div className="mt-8 space-y-3 max-w-md">
            <Toggle label="Round display" value={round} onChange={setRound} testid="device-toggle-round"/>
            <Toggle label="Show full body" value={showBody} onChange={setShowBody} testid="device-toggle-body"/>
            <Toggle label="Mute microphone (privacy)" value={micMuted} onChange={setMicMuted} testid="device-toggle-mic"/>
            <div className="rounded-2xl border border-white/10 p-4">
              <div className="flex items-center justify-between text-sm text-white/80">
                <span>Battery</span>
                <span className="font-mono">{battery}%</span>
              </div>
              <input type="range" min="5" max="100" value={battery} onChange={e=>setBattery(+e.target.value)} className="w-full accent-[var(--rammy-cyan)] mt-2" data-testid="device-battery-slider"/>
            </div>
          </div>
          <div className="mt-10 flex gap-3">
            <Link to="/app"><Button className="btn-pill btn-primary" data-testid="device-open-app">Open the app</Button></Link>
            <Link to="/admin"><Button variant="ghost" className="btn-pill border border-white/20 text-white hover:bg-white/10">Admin dashboard</Button></Link>
          </div>
        </section>

        <section className="flex items-center justify-center fade-up">
          {showBody ? (
            <div className="relative">
              <div className="absolute -inset-10 rounded-full bg-[var(--rammy-cyan)]/10 blur-3xl"/>
              <div className="relative"><RobotBody expression={expr} size={340}/></div>
              <div className="mt-4 flex justify-center gap-4 text-xs font-mono text-white/60">
                <span className="flex items-center gap-1"><Battery size={12}/>{battery}%</span>
                <span className="flex items-center gap-1"><Wifi size={12}/>Home-5G</span>
                <span>fw 1.0.4</span>
              </div>
            </div>
          ) : (
            <div className="relative">
              <div className="absolute -inset-10 rounded-full bg-[var(--rammy-cyan)]/10 blur-3xl"/>
              <div className="relative">
                <RobotFace expression={expr} size={420} round={round} battery={battery} micMuted={micMuted}
                  weather={<span><Sun size={10} className="inline mr-1"/>22°</span>}/>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function Toggle({ label, value, onChange, testid }) {
  return (
    <button onClick={()=>onChange(!value)} data-testid={testid}
      className="w-full flex items-center justify-between rounded-2xl border border-white/10 px-4 py-3 hover:border-white/25">
      <span className="text-sm">{label}</span>
      <span className={`w-10 h-6 rounded-full transition relative ${value ? "bg-[var(--rammy-cyan)]" : "bg-white/15"}`}>
        <span className={`absolute top-0.5 ${value ? "left-5" : "left-0.5"} w-5 h-5 bg-[var(--rm-surface)] rounded-full transition-all`}/>
      </span>
    </button>
  );
}
